import { z } from 'zod';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');

/**
 * Commissions et rémunérations (CDC §11). Chaque ligne de relevé est calculée depuis les
 * montants FIGÉS du dossier (§6 : snapshot à la validation), jamais depuis le barème vivant :
 *  - commission de VENTE : créée à la validation du dossier, pour le commercial du dossier ;
 *  - rémunération de POSE / ENTRETIEN : créée quand l'intervention termine le dossier, pour le
 *    technicien qui l'a réalisée ;
 *  - AJUSTEMENT : ligne manuelle (+/−) motivée, saisie par un responsable.
 * Un montant n'est jamais réécrit après validation : un écart donne une ligne d'ajustement.
 */
export const COMMISSION_KINDS = ['sale', 'install', 'adjustment', 'team'] as const;
export type CommissionKind = (typeof COMMISSION_KINDS)[number];
export const COMMISSION_KIND_LABELS: Record<CommissionKind, string> = {
  sale: 'Commission de vente',
  install: 'Rémunération de pose / entretien',
  adjustment: 'Ajustement',
  team: 'Commission d’encadrement (chef d’équipe)',
};

export const COMMISSION_STATUSES = ['to_validate', 'validated', 'paid', 'cancelled'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];
export const COMMISSION_STATUS_LABELS: Record<CommissionStatus, string> = {
  to_validate: 'À valider',
  validated: 'Validé',
  paid: 'Payé',
  cancelled: 'Annulé',
};

/** Transitions permises (responsable : `commission.manage`) ; `cancelled` et `paid` sont définitifs. */
export const COMMISSION_TRANSITIONS: Record<CommissionStatus, CommissionStatus[]> = {
  to_validate: ['validated', 'cancelled'],
  validated: ['paid', 'to_validate'],
  paid: [],
  cancelled: [],
};

export interface CommissionRecord {
  id: string;
  version: number;
  updatedAt: string;
  territoryId: string;
  beneficiaryId: string;
  beneficiaryName: string;
  /** société du bénéficiaire (sous-traitant), saisie sur sa fiche (Administration → Équipe) */
  beneficiaryCompany: string | null;
  kind: CommissionKind;
  /** vente / pose / entretien / sav (signature visuelle du type de dossier) */
  activity: string | null;
  dossierId: string | null;
  dossierReference: string | null;
  clientName: string | null;
  /** chantier pour un partenaire (sous-traitance de pose) : son nom, pour distinguer ces rémunérations */
  partnerName: string | null;
  /** produit / prestation, ou objet de l'ajustement */
  label: string;
  amountCents: number;
  /** date de rattachement (validation du dossier, fin d'intervention, saisie de l'ajustement) */
  earnedOn: string;
  /** mois du relevé AAAA-MM */
  period: string;
  status: CommissionStatus;
  validatedByName: string | null;
  validatedAt: string | null;
  paidAt: string | null;
  /** référence du paiement (n° de virement, n° de facture du sous-traitant…) */
  paymentReference: string | null;
  comment: string | null;
  /** intervention réalisée à plusieurs : à répartir entre les techniciens avant validation (null = rien à répartir) */
  crewInterventionId: string | null;
  createdAt: string;
}

export const commissionTransitionInput = z.object({
  ids: z.array(uuid).min(1).max(500),
  to: z.enum(COMMISSION_STATUSES),
  comment: z.string().trim().max(500).nullish().transform((v) => v || null),
  paymentReference: z.string().trim().max(100).nullish().transform((v) => v || null),
});

export const commissionAdjustmentInput = z.object({
  beneficiaryId: uuid,
  territoryId: uuid,
  dossierId: uuid.nullish().transform((v) => v ?? null),
  label: z.string().trim().min(2, 'Objet obligatoire').max(200),
  amountCents: z
    .number()
    .int()
    .refine((v) => v !== 0, 'Montant non nul')
    .refine((v) => Math.abs(v) <= 10_000_000, 'Montant trop élevé'),
  earnedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ'),
  comment: z.string().trim().min(3, 'Motif obligatoire').max(500),
});

export const periodOf = (day: string) => day.slice(0, 7);
export const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const s = new Date(y, m - 1, 15).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
export function shiftPeriod(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Totaux d'un relevé (vue permanente du mois §11) : total hors annulés, à valider, validé, payé. */
export function commissionTotals(entries: Pick<CommissionRecord, 'status' | 'amountCents'>[]) {
  const sum = (s: CommissionStatus) => entries.filter((e) => e.status === s).reduce((n, e) => n + e.amountCents, 0);
  const toValidate = sum('to_validate');
  const validated = sum('validated');
  const paid = sum('paid');
  return { totalCents: toValidate + validated + paid, toValidateCents: toValidate, validatedCents: validated, paidCents: paid, count: entries.filter((e) => e.status !== 'cancelled').length };
}

/** Onglets du profil mixte (§11) : Global / Ventes / Poses. */
export type CommissionTab = 'global' | 'sale' | 'install';
export function inTab(e: Pick<CommissionRecord, 'kind' | 'activity'>, tab: CommissionTab): boolean {
  if (tab === 'global') return true;
  if (tab === 'sale') return e.kind === 'sale' || ((e.kind === 'adjustment' || e.kind === 'team') && e.activity === 'vente');
  return e.kind === 'install' || ((e.kind === 'adjustment' || e.kind === 'team') && e.activity !== 'vente');
}

/** Colonnes de l'export mensuel (§11), conçu pour la facture du sous-traitant. */
export const COMMISSION_EXPORT_COLUMNS = ['Bénéficiaire', 'Nom client', 'Référence dossier', 'Date', 'Type de rémunération', 'Produit / prestation', 'Montant (€)', 'Statut'] as const;

/**
 * Répartition de la rémunération d'une intervention réalisée à plusieurs : un montant par technicien, libre (un
 * technicien peut toucher plus que l'autre ; 0 = rien). Total différent du barème du chantier : motif obligatoire.
 */
export const commissionSplitInput = z.object({
  interventionId: uuid,
  shares: z
    .array(z.object({ technicianId: uuid, amountCents: z.number().int().min(0).max(100_000_000) }))
    .min(1)
    .max(6),
  comment: z.string().trim().max(500).nullish().transform((v) => v || null),
});

export interface CommissionSplitInfo {
  interventionId: string;
  dossierReference: string | null;
  clientName: string | null;
  /** total à répartir (barème du chantier) */
  totalCents: number;
  technicians: { id: string; name: string; main: boolean; amountCents: number | null }[];
  /** déjà réparti (parts encore modifiables tant qu'aucune n'est validée) */
  split: boolean;
}
